'use client';
/**
 * Pending feedback for an individual `<Link>` (docs/10-ux-audit.md · UX-45).
 *
 * Both helpers must be rendered *inside* the link: they read Next's per-link status, which is only
 * available to the link's own children. The app-wide bar in `<NavigationProgress>` covers every
 * route change; these add the spinner right where the finger is, on the controls people press and
 * then wait on — the calls to action on the home page, the header, the album library.
 *
 * The spinner is invisible for the first ~120 ms (`k-link-pending`), so a navigation that is already
 * prefetched never flashes it.
 */
import * as React from 'react';
import { useLinkStatus } from 'next/link';
import { AlbumLoader } from './album-loader';
import { Spinner, cn } from '@freealbum/ui';

/** The link's icon, or a spinner for as long as that link's navigation is pending. */
export function LinkIcon({ children, className }: { children: React.ReactNode; className?: string }) {
  const { pending } = useLinkStatus();
  return pending ? <Spinner className={cn('k-link-pending-spinner', className)} /> : <>{children}</>;
}

/** For a link that is a picture rather than a row of text: a compact photo loader over the whole surface. */
export function LinkPendingOverlay({ label, className }: { label: string; className?: string }) {
  const { pending } = useLinkStatus();
  if (!pending) return null;
  return (
    <div className={cn('k-link-pending absolute inset-0 grid place-items-center bg-paper/90', className)}>
      <AlbumLoader size="compact" label={label} />
    </div>
  );
}
